import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  DataTable,
  Donut,
  Kpi,
  Legend,
  PageHead,
  RankedBars,
  Skeleton,
  Sparkline,
  StateBlock,
  TrendChart,
  seriesColor,
  type Column,
  type LegendItem,
} from '@w7/ui'
import { useDownline, useTurnover } from '@/app/queries'
import { AccountLink, Amount, AsOf, Money, deltaOf, exportCsv } from '@/app/bits'
import { EMPTY, count, dayLabel, ll, money, moneyCompact, percent } from '@/lib/format'
import { ROLE_LABEL, type Account } from '@/data/types'
import {
  FilteredEmpty,
  ReportFilters,
  ScopeBanner,
  ScopeUnavailable,
  scopeSuffix,
  scopedCsvName,
  useReportFilters,
  type Dimension,
} from './ReportFilters'

/**
 * Turnover — a screen the product being replaced did not have at all.
 *
 * It reported "turnover" in one column of one table and used the word
 * interchangeably with stake and with net amount, which is the single most
 * common reporting error in this domain: stake includes money that never found
 * the other side of a bet, and net amount is what changed hands after a market
 * settled. Neither is volume. This screen states the definition in the subtitle,
 * puts the unmatched money on a tile of its own so the difference is visible
 * rather than asserted, and then breaks the volume down by sport, by game
 * bucket, by agent and by member.
 */

/**
 * One page big enough to hold any subtree this console renders.
 *
 * `topMembers` carries each member's own Account and nothing about who they sit
 * under, and members are two or three rungs below the signed-in agent, so naming
 * their agent needs the subtree index. When the overview grows a parent field on
 * those rows this query goes away and nothing else on the screen changes.
 */
const AGENT_LOOKUP = { pageSize: 100_000 }

/** A row of `topMembers`, named locally so the column types below are explicit. */
interface MemberTurnoverRow {
  account: Account
  turnover: number
  netPnl: number
  bets: number
}

/** Stable empty reference, so the memos below do not rerun on every loading tick. */
const NO_MEMBERS: MemberTurnoverRow[] = []

/** Smoothing window for the trend overlay, and the least it may draw with. */
const TREND_WINDOW = 7
const MIN_TREND_POINTS = 5

/**
 * No member picker: this page is an aggregate of the whole book, and filtering
 * it to one member collapses every chart to a single bar — which is the Member
 * page's job, done better. Tier IS offered, but it narrows the by-agent card
 * alone; the panel says so rather than letting a chip appear to do nothing to
 * the tiles above it.
 */
const DIMENSIONS: Dimension[] = ['range', 'scope', 'sport', 'game', 'role', 'q']

export function TurnoverPage() {
  const filters = useReportFilters(DIMENSIONS)
  const { values, scopeAccount } = filters
  const range = values.range
  const { data, isLoading, dataUpdatedAt } = useTurnover({
    range: { from: range.from, to: range.to },
    ...(values.scope ? { rootId: values.scope } : {}),
    ...(values.sports.length ? { sports: values.sports } : {}),
    ...(values.games.length ? { games: values.games } : {}),
  })
  const downline = useDownline(AGENT_LOOKUP)
  const navigate = useNavigate()

  const asOf = data?.asOf ?? (dataUpdatedAt || undefined)

  /** id -> account for every account in scope, so a member's agent has a name. */
  const accountsById = useMemo(() => {
    const map = new Map<string, Account>()
    for (const account of downline.data?.rows ?? []) map.set(account.id, account)
    // The downline result excludes the root, and members hung directly off the
    // signed-in agent have that root as their parent.
    if (data?.account) map.set(data.account.id, data.account)
    // Live member-pnl carries parentId but not parent name — seed stubs so the
    // Agent column can still link rather than sit empty when hierarchy is off.
    for (const row of data?.topMembers ?? []) {
      map.set(row.account.id, row.account)
      const parentId = row.account.parentId
      if (parentId && !map.has(parentId)) {
        map.set(parentId, {
          ...row.account,
          id: parentId,
          username: parentId.slice(0, 8),
          displayName: parentId.slice(0, 8),
          role: 'master',
          parentId: null,
          path: [],
          memberCount: 0,
        })
      }
    }
    for (const row of data?.topAgents ?? []) map.set(row.account.id, row.account)
    return map
  }, [downline.data, data?.account, data?.topMembers, data?.topAgents])

  const series = useMemo(() => {
    if (!data) return null
    return {
      categories: data.daily.map((d) => dayLabel(d.date)),
      turnover: data.daily.map((d) => d.turnover),
      days: data.daily.length,
      // Days that actually saw a bet match, not days the range happens to span —
      // "30 of 30 days" says nothing, "22 of 30" says the book went quiet.
      activeDays: data.daily.filter((d) => d.bets > 0).length,
    }
  }, [data])

  const totals = data
    ? data.daily.reduce((acc, d) => ({ turnover: acc.turnover + d.turnover, bets: acc.bets + d.bets }), {
        turnover: 0,
        bets: 0,
      })
    : null

  const previousTotals = data
    ? data.previous.reduce((acc, d) => ({ turnover: acc.turnover + d.turnover, bets: acc.bets + d.bets }), {
        turnover: 0,
        bets: 0,
      })
    : null

  const avgBet = totals && totals.bets > 0 ? totals.turnover / totals.bets : null
  const previousAvgBet =
    previousTotals && previousTotals.bets > 0 ? previousTotals.turnover / previousTotals.bets : null
  const perMember = data && totals && data.activeMembers > 0 ? totals.turnover / data.activeMembers : null

  /**
   * Every figure on this page is volume, not profit. A bigger number is not a
   * better number — margin decides that, and margin lives on My P&L — so the
   * period-over-period changes are stated without a colour verdict.
   */
  const turnoverDelta = totals && previousTotals ? deltaOf(totals.turnover, previousTotals.turnover) : null
  const betsDelta = totals && previousTotals ? deltaOf(totals.bets, previousTotals.bets) : null
  const avgBetDelta = avgBet !== null && previousAvgBet !== null ? deltaOf(avgBet, previousAvgBet) : null

  /**
   * A centred average only yields a point where the whole window fits, so a
   * seven-day range produces exactly one — a dot sitting under a legend entry
   * promising a trend. Ask for enough centred points to read as a line before
   * offering the overlay at all.
   */
  const trendWindow =
    series && series.days - TREND_WINDOW + 1 >= MIN_TREND_POINTS ? TREND_WINDOW : undefined

  const trendLegend = useMemo<LegendItem[]>(
    () => [
      { label: 'Matched turnover that day', color: 'var(--w7-c1)' },
      ...(trendWindow ? [{ label: `${trendWindow}-day average`, color: 'var(--w7-text-3)' }] : []),
    ],
    [trendWindow],
  )

  /**
   * `bySport` carries a row for any sport that merely SETTLED in the window, and
   * those can have zero matched volume. The donut drops zero-value slices, so
   * colours are pinned to the sport here rather than left to ring position —
   * otherwise the legend's swatches slide off the sports they name.
   * Live analytics has no sport dimension — `sportUnavailable` short-circuits.
   */
  const bySport = useMemo(() => {
    if (!data || data.sportUnavailable) return null
    const rows = data.bySport
      .filter((s) => s.turnover > 0)
      .map((s, i) => ({ sport: s.sport, turnover: s.turnover, color: seriesColor(i) }))
    return { rows, total: rows.reduce((sum, s) => sum + s.turnover, 0) }
  }, [data])

  /** Fixture game buckets — live path uses `byMarket` (OpenAPI groupBy=market). */
  const byGame = useMemo(() => {
    if (!data || data.sportUnavailable) return null
    return data.byGame.filter((g) => g.turnover > 0)
  }, [data])

  const byMarket = useMemo(() => {
    if (!data || !data.sportUnavailable) return null
    return data.byMarket.filter((m) => m.turnover > 0)
  }, [data])

  /* Tier is applied HERE, over `topAgents`, because that is the only place on
     this page where a row has an agent tier at all. Everything above is rolled
     up across the whole book. */
  const agents = useMemo(() => {
    if (!data) return null
    const rows = values.roles.length
      ? data.topAgents.filter((a) => values.roles.includes(a.account.role))
      : data.topAgents
    return rows.slice(0, 10)
  }, [data, values.roles])

  /* Text narrows the member leaderboard only — `OverviewQuery` has no free-text
     field, and inventing one client-side for the tiles would make them disagree
     with the chart directly above them. */
  const memberNeedle = values.q.trim().toLowerCase()
  const members: MemberTurnoverRow[] = useMemo(() => {
    const rows = data?.topMembers ?? NO_MEMBERS
    if (!memberNeedle) return rows
    return rows.filter(
      (row) =>
        row.account.username.toLowerCase().includes(memberNeedle) ||
        row.account.displayName.toLowerCase().includes(memberNeedle),
    )
  }, [data?.topMembers, memberNeedle])

  /** Totals for the rows actually shown, which is a top slice and not the period. */
  const shownTotals = useMemo(() => {
    if (members.length === 0) return null
    return members.reduce(
      (acc, r) => ({
        bets: acc.bets + r.bets,
        turnover: acc.turnover + r.turnover,
        netPnl: acc.netPnl + r.netPnl,
      }),
      { bets: 0, turnover: 0, netPnl: 0 },
    )
  }, [members])

  const agentsLoading = downline.isLoading

  const columns = useMemo<Column<MemberTurnoverRow>[]>(
    () => [
      {
        id: 'member',
        header: 'Member',
        cell: (row) => <AccountLink id={row.account.id} username={row.account.username} />,
        sortValue: (row) => row.account.username,
      },
      {
        id: 'agent',
        header: 'Agent',
        help: 'The agent this member sits directly under.',
        cell: (row) => {
          const agent = row.account.parentId ? accountsById.get(row.account.parentId) : undefined
          if (!agent) return agentsLoading ? <Skeleton width="60%" /> : EMPTY
          return (
            <span className="w7-row" style={{ gap: 6, minWidth: 0 }}>
              <AccountLink id={agent.id} username={agent.username} />
              <span className="w7-dimmer" style={{ fontSize: 11 }}>
                {ROLE_LABEL[agent.role]}
              </span>
            </span>
          )
        },
        sortValue: (row) =>
          (row.account.parentId ? accountsById.get(row.account.parentId)?.username : null) ?? null,
        hideBelow: 900,
      },
      {
        id: 'bets',
        header: 'Bets',
        numeric: true,
        help: 'Bets that found a match. An unmatched bet produces no turnover and is not counted here.',
        cell: (row) => count(row.bets),
        sortValue: (row) => row.bets,
        total: shownTotals ? count(shownTotals.bets) : undefined,
        hideBelow: 720,
      },
      {
        id: 'turnover',
        header: 'Turnover',
        numeric: true,
        help: 'Matched volume this member produced in the period.',
        cell: (row) => <Amount value={row.turnover} />,
        sortValue: (row) => row.turnover,
        total: <Amount value={shownTotals?.turnover} />,
      },
      {
        id: 'net',
        header: 'Net to book',
        numeric: true,
        help: 'What the book kept once this member’s settled markets were netted off. Green is money the book won, red is money it paid out.',
        cell: (row) => <Money value={row.netPnl} signed />,
        sortValue: (row) => row.netPnl,
        total: <Money value={shownTotals?.netPnl} signed />,
      },
      {
        id: 'average',
        header: 'Average bet',
        numeric: true,
        help: 'Turnover divided by matched bets — the average size that actually matched, not the average stake submitted.',
        cell: (row) => <Amount value={row.bets > 0 ? row.turnover / row.bets : null} />,
        sortValue: (row) => (row.bets > 0 ? row.turnover / row.bets : null),
        total: (
          <Amount
            value={shownTotals && shownTotals.bets > 0 ? shownTotals.turnover / shownTotals.bets : null}
          />
        ),
        hideBelow: 1080,
      },
    ],
    [accountsById, agentsLoading, shownTotals],
  )


  const dailyRows = data?.daily ?? []
  const onExport = () =>
    exportCsv(
      scopedCsvName('turnover', scopeAccount, range),
      // Money is written in the same grouped Indian format the screen shows: the
      // file gets opened next to this page and the two must agree digit for digit.
      dailyRows.map((d) => ({
        Date: ll(d.date),
        Turnover: money(d.turnover, { symbol: false }),
        'Matched bets': d.bets,
        'Average bet': money(d.bets > 0 ? d.turnover / d.bets : null, { symbol: false }),
        'Active members': d.activeMembers,
      })),
    )

  const head = (
    <PageHead
      title="Turnover"
      subtitle={`Matched volume — the money that actually found the other side of a bet. It is not stake, which still counts money sitting unmatched, and it is not net amount, which is what changed hands once a market settled. · ${range.label}${scopeSuffix(scopeAccount)}`}
    />
  )

  /* `getOverview` returns null rather than falling back to self when the scope
     is not beneath the caller — its `account` field names whose book this is,
     and drawing your numbers under their name is the one failure a scoped
     report must not have. Either signal (the account read or the snapshot)
     lands on the same block. */
  if (filters.scopeInvalid || (values.scope && data === null)) {
    return (
      <>
        {head}
        <ScopeUnavailable onClear={() => filters.clearDimension('scope')} />
      </>
    )
  }

  return (
    <>
      {head}

      <ReportFilters
        state={filters}
        textLabel="Filter the member leaderboard"
        textPlaceholder="Member username or name"
        roleLabel="Agent tier"
        roleNote="Tier narrows the by-agent breakdown only. Every other figure on this page is the whole book rolled up, where an agent tier does not exist."
        omission="No member picker and no direction: this page is an aggregate of the whole book, and narrowing it to one member collapses every chart to a single bar — that is the Member page. A matched bet has no give or take direction."
        actions={
          <>
            <AsOf at={asOf} />
            {data?.martStale ? (
              <span className="w7-dimmer" style={{ fontSize: 12 }}>
                Mart marked stale
              </span>
            ) : null}
            <Button
              icon="download"
              variant="secondary"
              onClick={onExport}
              disabled={dailyRows.length === 0}
            >
              Export CSV
            </Button>
          </>
        }
      />

      {scopeAccount ? (
        <ScopeBanner account={scopeAccount} onClear={() => filters.clearDimension('scope')} />
      ) : null}

      {/* ---------------------------------------------------------- KPIs -- */}
      <div className="w7-grid w7-grid-6" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Kpi
          label="Total turnover"
          value={totals ? moneyCompact(totals.turnover) : EMPTY}
          exact={money(totals?.turnover)}
          accent
          delta={turnoverDelta?.text}
          deltaTone="neutral"
          spark={
            series && series.turnover.length > 1 ? (
              // Not MiniSpark: it colours by direction, and rising volume is
              // neither a win nor a loss for the book.
              <Sparkline values={series.turnover} width={64} height={22} color="var(--w7-c1)" />
            ) : undefined
          }
          meta="Matched volume across your whole downline in this period"
        />
        <Kpi
          label="Matched bets"
          value={totals ? count(totals.bets) : EMPTY}
          delta={betsDelta?.text}
          deltaTone="neutral"
          meta={
            // "1 of 1 days" is noise on the single-day range, so it says what a
            // matched bet is instead.
            !series
              ? undefined
              : range.days === 1
                ? 'Bets that found the other side today'
                : `Matched on ${count(series.activeDays)} of ${count(range.days)} days`
          }
        />
        <Kpi
          label="Average bet size"
          value={avgBet === null ? EMPTY : moneyCompact(avgBet)}
          exact={money(avgBet)}
          delta={avgBetDelta?.text}
          deltaTone="neutral"
          meta="Turnover divided by matched bets — the average size that matched, not the average stake submitted"
        />
        <Kpi
          label="Active members"
          value={data ? count(data.activeMembers) : EMPTY}
          meta={
            data
              ? data.totalMembers != null
                ? `of ${count(data.totalMembers)} beneath you · a member counts once they match a bet`
                : 'Members who matched a bet in this period (downline headcount not on this mart)'
              : undefined
          }
        />
        <Kpi
          label="Turnover per active member"
          value={perMember === null ? EMPTY : moneyCompact(perMember)}
          exact={money(perMember)}
          meta="Average matched volume for each member who actually bet"
        />
        {/* The one tile here that is a live position rather than a period figure,
            and it earns its place: it is exactly the money the other five tiles
            do not count, which is what makes turnover ≠ stake concrete. */}
        <Kpi
          label="Stake still unmatched"
          value={
            data == null || data.unmatchedStake == null
              ? EMPTY
              : moneyCompact(data.unmatchedStake)
          }
          exact={data?.unmatchedStake == null ? undefined : money(data.unmatchedStake)}
          meta={
            data?.livePositionUnavailable
              ? 'Not in the analytics contract yet — live unmatched stake needs exposure, not period turnover'
              : data
                ? `Across ${count(data.openBets)} bets open right now · staked money that has not become turnover`
                : undefined
          }
        />
      </div>

      {/* --------------------------------------------------------- trend -- */}
      <Card
        title="Daily matched turnover"
        subtitle={
          trendWindow
            ? 'Each day on its own, with a centred seven-day average through it so one busy Saturday does not read as a trend.'
            : 'Each day on its own. Choose a longer range to get the seven-day average overlay.'
        }
        style={{ marginBottom: 'var(--w7-s5)' }}
      >
        {series ? (
          <>
            <TrendChart
              label="Matched turnover by day, with a seven-day moving average"
              categories={series.categories}
              height={260}
              format={(v) => moneyCompact(v, false)}
              // 6, not the kit's default 8: the axis always labels the last day
              // on top of its regular stride, and over a 30-day range a stride of
              // 4 puts that final label hard against the one before it.
              maxLabels={6}
              trendWindow={trendWindow}
              series={[
                {
                  id: 'turnover',
                  label: 'Turnover',
                  values: series.turnover,
                  kind: 'area',
                  color: 'var(--w7-c1)',
                },
              ]}
              emptyMessage="No bet was matched in this period"
            />
            <Legend items={trendLegend} />
          </>
        ) : (
          <ChartSkeleton height={260} />
        )}
      </Card>

      {/* ------------------------------------------------------ breakdown -- */}
      <div className="w7-grid w7-grid-2" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card
          title="Turnover by sport"
          subtitle="Every bet matched in this period, grouped by the sport it was placed on"
        >
          {data?.sportUnavailable ? (
            <StateBlock icon="turnover" title="Unavailable — no market catalogue">
              Sport is not a dimension in analytics (`groupBy` is agent|market only). Odds markets
              carry id and name — inventing a sport axis would look finished and be wrong.
            </StateBlock>
          ) : bySport === null ? (
            <ChartSkeleton height={200} />
          ) : values.sports.length > 0 && bySport.rows.length <= 1 ? (
            /* A donut with one slice is a filled circle, which reads as a
               rendering fault rather than as "you filtered this to one thing".
               Say what happened instead of drawing it. */
            <StateBlock icon="filter" title={`Filtered to ${values.sports.join(', ')}`}>
              Clear the sport filter to compare sports. The tiles and the daily chart above are
              filtered the same way.
            </StateBlock>
          ) : (
            <>
              {/* No centre total on purpose. This ring rolls the bets up itself,
                  while the tile above comes from the book's own daily series, and
                  two grand totals a few thousand rupees apart in the same eyeline
                  read as a defect. The legend carries every figure that matters. */}
              <Donut
                label="Matched turnover by sport"
                height={200}
                slices={bySport.rows.map((s) => ({ label: s.sport, value: s.turnover, color: s.color }))}
                format={(v) => money(v, { round: true })}
                emptyMessage="No bet was matched in this period"
              />
              <Legend
                items={bySport.rows.map((s) => ({
                  label: s.sport,
                  color: s.color,
                  value: `${moneyCompact(s.turnover)} · ${percent(
                    bySport.total > 0 ? s.turnover / bySport.total : null,
                    0,
                  )}`,
                }))}
              />
            </>
          )}
        </Card>

        {data?.sportUnavailable ? (
          <Card
            title="Turnover by market"
            subtitle="Matched volume from GET /turnover?groupBy=market. Labels are market ids until a catalogue can resolve names — the API omits a fake name rather than echoing the id as a title."
          >
            {byMarket === null ? (
              <ChartSkeleton height={200} />
            ) : byMarket.length === 0 ? (
              <StateBlock icon="turnover" title="No market produced volume in this period">
                A market lands here once a bet against it matches. Sport and game buckets are not in
                the contract; this is the only product-shaped breakout analytics can serve today.
              </StateBlock>
            ) : (
              <RankedBars
                label="Markets by matched turnover"
                rows={byMarket.slice(0, 12).map((m) => ({
                  label: m.label,
                  value: m.turnover,
                  note: m.bets > 0 ? `${count(m.bets)} bets` : undefined,
                }))}
                format={(v) => moneyCompact(v)}
                labelWidth={148}
                emptyMessage="No market produced volume in this period"
              />
            )}
          </Card>
        ) : (
          <Card
            title="Turnover by game"
            subtitle="Grouped by the game bucket the market belongs to. A bucket is only known once a market settles, so this adds up to less than the matched volume above — the difference is turnover on markets that are still running."
          >
            {byGame === null ? (
              <ChartSkeleton height={200} />
            ) : values.games.length > 0 && byGame.length <= 1 ? (
              <StateBlock icon="filter" title={`Filtered to ${values.games.join(', ')}`}>
                Clear the game filter to compare buckets. The tiles and the daily chart above are
                filtered the same way.
              </StateBlock>
            ) : byGame.length === 0 ? (
              <StateBlock icon="turnover" title="Nothing has settled in this period">
                A game bucket appears here once one of the markets your members bet on settles. Open
                Exposure to see the positions that are still running.
              </StateBlock>
            ) : (
              <RankedBars
                label="Turnover by game bucket"
                rows={byGame.map((g) => ({ label: g.game, value: g.turnover }))}
                format={(v) => moneyCompact(v)}
                labelWidth={148}
                emptyMessage="Nothing has settled in this period"
              />
            )}
          </Card>
        )}
      </div>

      {/* ------------------------------------------------------- by agent -- */}
      <Card
        title="Turnover by agent"
        subtitle="Your direct downline agents, each carrying the matched volume of everyone beneath them. Members you hold directly are counted in the totals above but have no agent to sit under here."
        style={{ marginBottom: 'var(--w7-s5)' }}
      >
        {agents === null ? (
          <ChartSkeleton height={220} />
        ) : agents.length === 0 && values.roles.length > 0 ? (
          <StateBlock
            icon="filter"
            title="No agent of that tier produced volume in this period"
            actions={
              <Button variant="secondary" icon="close" onClick={() => filters.clearDimension('role')}>
                Clear the tier filter
              </Button>
            }
          >
            Tier narrows this card only — every figure above it still covers the whole book.
          </StateBlock>
        ) : agents.length === 0 ? (
          <StateBlock icon="downline" title="You hold no agents yet">
            Every account you create is either an agent, who can build a downline of their own, or a
            member, who bets. Create an agent and the volume their downline produces appears here.
          </StateBlock>
        ) : (
          <RankedBars
            label="Agents by matched turnover"
            rows={agents.map((a) => ({
              label: a.account.username,
              value: a.turnover,
              note: `${count(a.members)} members`,
              onClick: () => navigate(`/member/${a.account.id}`),
            }))}
            format={(v) => moneyCompact(v)}
            emptyMessage="No agent produced volume in this period"
          />
        )}
      </Card>

      {/* ------------------------------------------------------ by member -- */}
      <Card
        title="Top members by turnover"
        subtitle="The members who matched the most volume in this period, biggest first. Turnover is volume and carries no colour; net to book is the money side — green is what the book won, red is what it paid out."
        flush
      >
        <DataTable
          label="Top members by matched turnover"
          rows={members}
          columns={columns}
          rowKey={(row) => row.account.id}
          loading={isLoading}
          skeletonRows={10}
          showTotals={Boolean(shownTotals)}
          totalsLabel={`Top ${members.length}`}
          initialSort={{ columnId: 'turnover', direction: 'desc' }}
          // No whole-row click: the Member and Agent cells are already links, and
          // a clickable row wrapping them puts one tab stop inside another.
          empty={
            filters.isFiltered ? (
              <FilteredEmpty state={filters} icon="turnover" />
            ) : (
              <StateBlock icon="turnover" title="No member matched a bet in this period">
                A member lands here as soon as one of their bets finds the other side. Widen the
                date range, or open Downline to see who has been created but has not bet yet.
              </StateBlock>
            )
          }
        />
      </Card>
    </>
  )
}

/** Chart-sized loading block. Flex makes the kit's inline skeleton span fill it. */
function ChartSkeleton({ height }: { height: number }) {
  return (
    <div style={{ display: 'flex', height }} aria-hidden="true">
      <Skeleton height="100%" radius={10} />
    </div>
  )
}
